<template>
  <div
    class="hero-section-wrapper" :class="[className, $config.project, $config.platform]"
  >
    <div
      class="container"
      :class="{ 'image-container': imageUrl || $slots.image }"
    >
      <div class="row">
        <div
          :class="[offset ? `offset-${offset}` : '', columns ? columns : '', mdcolumns ? mdcolumns : '', mdoffset ? `offset-${mdoffset}` : '']"
        >
          <div class="heading col">
            <h2 v-if="titleHighlight" class="h3" v-html="title" />
            <h2 v-else-if="titleH2" class="h2">
              {{ title }}
            </h2>
            <h1 v-else :class="[titleClass]">
              {{ title }}
            </h1>
            <p
              v-if="className === 'qa' && subtitle"
              class="body-2"
              v-html="subtitle"
            />
            <p
              v-else-if="subtitle"
              class="subtitle"
            >
              {{ subtitle }}
            </p>
            <p
              v-if="desc"
              class="body-2"
            >
              {{ desc }}
            </p>
            <slot name="stats" />
            <slot name="middle" />
            <template v-if="button || buttonTitle && buttonLink">
              <ButtonArrow
                v-if="innerPosition"
                :text="buttonTitle"
                type="nuxt-link"
                :to="buttonLink"
                class="font-18"
              />
              <button
                v-else-if="button && !buttonLink" class="btn-primary btn-lg"
                @click="$emit('buttonClicked')"
              >
                {{ buttonTitle }}
              </button>
              <nuxt-link
                v-else
                :to="buttonLink"
                class="btn-primary btn-lg"
                data-event="start_your_career_1.click"
              >
                {{ buttonTitle }}
              </nuxt-link>
            </template>
            <slot name="bottom" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="imageUrl || $slots.image"
      class="wall"
    >
      <client-only v-if="imageUrl">
        <img
          :key="$store.state.layout"
          :src="image"
          :alt="imageUrl"
        >
      </client-only>
      <slot v-else name="image" />
    </div>
  </div>
</template>

<script>
import ButtonArrow from './ButtonArrow';

export default {
  components: {
    ButtonArrow,
  },
  props: {
    title: { type: String, required: true },
    titleHighlight: Boolean,
    titleClass: { type: String, default: 'h2', required: false },
    subtitle: { type: String, default: '', required: false },
    desc: { type: String, default: '', required: false },
    buttonTitle: { type: String, default: '', required: false },
    buttonLink: { type: String, default: '', required: false },
    button: { type: Boolean },
    innerPosition: { type: Boolean },
    offset: { type: Number, default: 0 },
    mdoffset: { type: Number, default: 0 },
    imageUrl: { type: String, default: '' },
    columns: { type: String, default: 'col-4' },
    mdcolumns: { type: String, default: '' },
    className: { type: String, default: '' },
    titleH2: Boolean,
  },
  computed: {
    image() {
      if (this.$config.platform === 'fa') {
        // eslint-disable-next-line import/no-dynamic-require
        return require(`../assets/images/fa/${this.imageUrl}`);
      }

      // eslint-disable-next-line import/no-dynamic-require
      return require(`../assets/images/${this.imageUrl}`);
    },
  },
};
</script>

<style lang="scss">
.tox-dialog__disable-scroll {
  .hero-section-wrapper.qa .wall:after {
    transition-delay: 2ms;
    height: 0;
  }
}

.hero-section-wrapper {
  background-color: var(--main-dark);
  position: relative;
  max-width: 100%;

  &.lms {
    .wall,
    .container.image-container,
    .container:after {
      height: 544px;
    }
  }

  &.qa {
    .container .row > div {
      .body-2 {
        margin-bottom: var(--spacing-28);

        a {
          color: var(--link-color);
          font-weight: 600;
        }
      }
    }

    &.lms .container .row > div {
      h1 {
        font-size: 38px;
      }

      .btn-primary {
        max-width: 166px;
      }
    }

    .wall:after {
      content: '';
      top: 0;
      left: -150px;
      background: linear-gradient(90deg, rgba(0, 12, 31, 0.94) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 56.77%, rgba(0, 12, 31, 0) 100%);
      position: absolute;
      transition-delay: 4s;
      height: 100%;
      width: 300px;
      z-index: 2;
    }
  }

  &.certificates,
  &.practice-exams {
    .wall:after {
      content: '';
      top: 0;
      left: -150px;
      background: linear-gradient(90deg, rgba(0, 12, 31, 0.94) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 56.77%, rgba(0, 12, 31, 0) 100%);
      position: absolute;
      height: 100%;
      width: 300px;
      z-index: 2;
    }
  }

  &.verify-certificate-hero {
    &.right-shadow {
      .wall:after {
        content: '';
        top: 0;
        right: 0;
        position: absolute;
        height: 100%;
        width: 60%;
        z-index: 2;
        background: linear-gradient(90deg, var(--main-dark) 0%, var(--main-dark) 50%, rgba(1, 22, 51, 0) 100%);
        transform: rotate(-180deg);
      }
    }
  }

  &.left-shadow {
    .wall:before {
      content: '';
      top: 0;
      left: -130px;
      background-color: var(--main-dark);
      position: absolute;
      height: 100%;
      width: 200px;
      z-index: 2;
    }
  }

  &.center-gradient {
    .wall:before {
      background: linear-gradient(90deg, var(--main-dark) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.7) 55.73%, rgba(0, 12, 31, 0) 100%);
      left: 0;
    }
  }

  &.instructors-shadow {
    .image-container:after {
      width: 580px;

      @media screen and (min-width: 1681px) and (max-width: 1920px) {
        width: 340px;
      }

      @media screen and (min-width: 1441px) and (max-width: 1680px) {
        background: linear-gradient(90deg, rgba(0, 12, 31, 0.94) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 75.77%, rgba(0, 12, 31, 0) 100%);
        width: 460px;
      }

      @media screen and (min-width: 1281px) and (max-width: 1440px) {
        background: linear-gradient(90deg, rgba(0, 12, 31, 0.94) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 66.77%, rgba(0, 12, 31, 0) 100%);
        width: 650px;
      }
    }

    .wall:before {
      content: '';
      top: 0;
      left: 0;
      background: linear-gradient(90deg, rgba(0, 12, 31, 0.94) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 56.77%, rgba(0, 12, 31, 0) 100%);
      position: absolute;
      height: 100%;
      width: 250px;
    }
  }

  &.tiny {
    overflow: hidden;

    &:before {
      background: linear-gradient(253.23deg, rgba(0, 164, 178, 0.2) 0%, rgba(0, 164, 178, 0.05) 44.75%, rgba(0, 164, 178, 0) 94.77%);
      transform: matrix(-1, 0, 0, 1, 0, 0);
      content: '';
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }

    &:after {
      opacity: 0;
    }
  }

  &.affiliate {
    .wall,
    .container.image-container {
      height: 820px;
    }

    &:after,
    &:before,
    .container:after {
      display: none;
    }

    .container {
      .row {
        > div {
          h1 {
            margin-bottom: var(--spacing-4);
          }

          .subtitle {
            margin: 0;
            color: var(--main-light);
          }

          .body-2 {
            max-width: 470px;
            margin-top: var(--spacing-24);
            margin-bottom: var(--spacing-24);
          }

          .btn-primary {
            max-width: 350px;
          }
        }
      }
    }
  }

  &.live-trainings {
    background: linear-gradient(106.99deg, rgba(86, 191, 199, 0.2) 10.8%, rgba(86, 191, 199, 0) 61.7%), var(--main-dark);

    &:before {
      top: unset;
      bottom: 0;
      height: 152px;
      background: linear-gradient(0deg, rgba(0, 12, 31, 0.8) 0%, rgba(0, 12, 31, 0) 100%);
    }

    &:after {
      display: none;
    }

    .container {
      &:after {
        display: none;
      }

      .row {
        > div {
          padding-top: 32px;
          text-align: center;
          align-items: flex-start;

          .h3 {
            span {
              color: var(--accent);
            }
          }

          .subtitle {
            max-width: 800px;
            font-size: 16px;
            margin: 0 auto;
          }
        }
      }
    }

    .wall {
      left: 0;
      z-index: 1;
      overflow: hidden;

      &:before {
        content: '';
        position: absolute;
        top: 0;
        left: -50px;
        width: 152px;
        height: 100%;
        background: linear-gradient(90deg, rgba(0, 12, 31, 0.8) 0%, rgba(0, 12, 31, 0) 100%);
        z-index: 1;
      }

      &:after {
        content: '';
        position: absolute;
        top: 0;
        right: -50px;
        width: 152px;
        height: 100%;
        background: linear-gradient(270deg, rgba(0, 12, 31, 0.8) 0%, rgba(0, 12, 31, 0) 100%);
        z-index: 1;
      }

      img {
        position: absolute;
        bottom: 32px;
        left: 50%;
        transform: translateX(-50%);
        height: auto;
      }
    }

    &.lms .container .row > div {
      padding-top: 64px;
    }

    &.site {
      .container {
        .row {
          > div {
            .h3 {
              font-size: 44px;
            }

            .subtitle {
              font-size: 21px;
              margin-bottom: var(--spacing-32);
            }
          }
        }
      }

      .wall {
        img {
          height: auto;
        }
      }
    }
  }

  .wall {
    position: absolute;
    right: 0;
    top: 0;
    height: 650px;
    width: auto;
    z-index: 0;

    img {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  }

  &:before,
  &:after {
    content: '';
    left: 0;
    position: absolute;
    width: 100%;
    z-index: 2;
  }

  &:before {
    background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
    height: 110px;
    top: 0;
  }

  &:after {
    bottom: 0;
    background: linear-gradient(360deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
    height: 200px;
  }

  .container {
    position: relative;
    align-items: center;
    min-height: 380px;

    &:after {
      background: linear-gradient(90deg, rgba(0, 12, 31, 0.94) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 56.77%, rgba(0, 12, 31, 0) 100%);
      content: '';
      left: 0;
      height: 650px;
      top: 0;
      position: absolute;
      width: 700px;
      z-index: 1;
    }

    &.image-container {
      height: 650px;
    }

    .row {
      align-items: center;
      height: 100%;

      > div {
        height: inherit;
        display: flex;
        align-items: center;
        position: relative;
        color: var(--main-light);
        z-index: 5;
        min-height: 380px;

        .h2 {
          margin: 0 auto var(--spacing-16);
        }

        .subtitle {
          font-weight: 500;
          color: var(--main-light-shade-4);
          margin-top: 0;
        }

        .body-2 {
          color: var(--main-light-shade-4);
          margin: 0;
        }

        .btn-primary {
          max-width: 300px;
          width: 100%;
        }

        .font-18 {
          font-size: 18px;
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .hero-section-wrapper {
    &.qa.lms,
    &.career-tracks-page {
      .container .row > div .h2 {
        font-size: 32px;
      }
    }

    &.career-track-certificates,
    &.certificate-page,
    &.qa {
      .wall img {
        object-fit: cover;
        width: 100%;
      }

      .wall:after {
        content: '';
        top: 0;
        left: -1px;
        background: var(--main-dark);
        position: absolute;
        height: 100%;
        width: 300px;
        z-index: 2;
      }
    }

    &.instructors-shadow .wall:before {
      background: var(--main-dark);
    }

    .container {
      .row {
        > div {
          .btn-primary {
            max-width: 225px;
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .hero-section-wrapper {
    &.certificates .container:after,
    &.verify-certificate .container:after {
      width: 65%;
    }

    &.left-shadow .wall:before {
      height: 0;
    }

    &.instructors-shadow {
      .heading {
        width: 400px;
      }

      .wall:after {
        display: none;
      }
    }

    &.affiliate {
      &:after,
      &:before,
      .container:after {
        display: block;
      }

      .container:after {
        height: 820px;
      }

      .wall {
        img {
          object-position: 80%;
        }
      }
    }

    &.live-trainings {
      .heading {
        width: 100%;
      }

      .wall {
        img {
          object-position: 50%;
        }
      }

      &.site {
        .wall {
          img {
            width: auto;
          }
        }
      }
    }

    .container {
      &:after {
        width: 50%;
      }
    }

    .heading {
      width: 60%;
      flex: 0 0 auto;
    }

    .wall {
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: 40%;
      }
    }
  }
}

@include bp($bp-phone) {
  .hero-section-wrapper,
  .hero-section-wrapper.lms {
    padding-top: 450px;

    &.certificates .wall img {
      object-position: 65%;
    }

    &.qa .wall img {
      object-position: 60%;
    }

    &.instructors-shadow {
      .heading {
        width: 100%;
      }

      .image-container:after {
        width: 100%;
      }

      .wall {
        img {
          object-position: 95%;
        }

        &:before {
          background: transparent;
        }
      }
    }

    &.verify-certificate {
      padding-top: 500px;

      .container {
        padding-bottom: 112px !important;

        &:after {
          bottom: 0;
          top: -130px;
          height: 228px;
          width: 100%;
          background: linear-gradient(0deg, var(--main-dark) 0%, var(--main-dark) 0.01%, rgba(0, 12, 31, 0.98) 56.77%, rgba(0, 12, 31, 0) 100%);
        }

        .heading .h2 {
          margin-bottom: var(--spacing-48);
          font-size: 32px;
        }
      }

      .wall {
        top: 100px;
        height: 380px;

        img {
          object-position: 70%;
        }
      }
    }

    &.lms {
      min-height: 748px;
      padding-top: 0;
      display: flex;
      flex-direction: column;

      .wall {
        position: static;
        order: -1;
      }

      .image-container {
        height: auto;
        min-height: auto;

        .row div {
          min-height: auto;
        }
      }
    }

    &.affiliate {
      padding-top: 370px;
      text-align: center;

      .container.image-container {
        height: auto;
        padding-bottom: 45px;

        &:after {
          height: 150px;
          top: -50px;
        }
      }

      .wall {
        height: 450px;
      }

      &:before {
        display: none;
      }
    }

    &.live-trainings {
      background: transparent;
      background-color: var(--main-dark);

      .container.image-container {
        padding-bottom: 65px;

        .row {
          > div {
            .h3 {
              font-size: 28px;
              margin-bottom: var(--spacing-12);
            }

            .subtitle {
              font-size: 14px;
              font-weight: 500;
            }
          }
        }
      }

      .wall {
        order: 1;
        height: auto;

        &:before,
        &:after {
          display: none;
        }

        img {
          position: static;
          transform: translate(0);
        }
      }

      &.site {
        padding-top: 0;

        .container.image-container {
          padding-bottom: 33px;

          .row {
            > div {
              .h3 {
                font-size: 40px;
              }

              .subtitle {
                font-size: 18px;
                font-weight: 400;
              }

              .btn-primary {
                margin-top: var(--spacing-32);
              }
            }
          }
        }

        .wall {
          height: auto;
          position: static;
          padding-bottom: var(--spacing-40);

          img {
            width: 100%;
          }
        }
      }
    }

    .heading {
      width: 100%;

      .subtitle {
        padding: 0;
        margin: 0 !important;
      }

      .btn-arrow {
        margin-top: var(--spacing-48);
      }
    }

    &:after {
      display: none;
    }

    .wall {
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      height: 450px;
      width: 100%;
      overflow: hidden;

      img {
        object-position: 75%;
      }

      &:after {
        background: linear-gradient(0deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
        bottom: auto;
        top: 320px;
        left: 0;
        height: 130px;
        width: 100%;
      }
    }

    .container,
    .container.image-container {
      height: auto;
      min-height: auto;
      padding-bottom: var(--spacing-80);

      .row {
        > div {
          min-height: auto;

          .btn-primary {
            max-width: 100%;
            margin-top: var(--spacing-48);
          }
        }
      }
    }

    .container:after {
      top: auto;
      bottom: 100%;
      width: 100%;
      height: 150px;
      background: linear-gradient(0deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
    }

    &.tiny {
      padding: 0;

      .container {
        padding: 0 var(--spacing-24);
        min-height: 333px;

        .row {
          align-items: center;
          height: 100%;

          > div {
            min-height: 333px;
            padding-top: 68px;

            .h2 {
              margin-bottom: 10px;
            }
          }
        }
      }
    }

    &.qa {
      .container.image-container {
        background: var(--main-dark);
      }
    }
  }
}

.fa {
  @import '../assets/sass/fa/components/herosection.scss';
}
</style>
